<template>
    <div class="graph-page">
        <div class="row">
          <h3>Graph</h3>
          <div class="refresh" @click="getDataForCharts(1)">
          </div>
        </div>
        <form>
          <div class="activePlaceholderImitation">
              <datepicker
                v-model="filters.startDate"
                calendar-class='calendar'
                :disabled-dates="{from: new Date(filters.endDate)}"
                :use-utc='true'
              ></datepicker>
              <span>Start date</span>
            </div>
            <div class="activePlaceholderImitation">
              <datepicker
                v-model="filters.endDate"
                calendar-class='calendar'
                :disabled-dates="{to: new Date(filters.startDate)}"
                :use-utc='true'
              ></datepicker>
              <span>End date</span>
            </div>
            <div class="selectWrapper activePlaceholder" :class="{'selected':filters.interval}">
                <v-select 
                    v-model="filters.interval" 
                    :options="intervalsList"
                    :reduce="obj => obj.code" 
                    :clearable="false">
                </v-select>
                <span>Interval</span>
            </div>
            <div class="selectWrapper activePlaceholder" :class="{'selected':filters.exchange}">
              <v-select v-model="filters.exchange" :options="exchangesList" disabled></v-select>
              <span>Exchange</span>
            </div>
            <div class="selectWrapper activePlaceholder" :class="{'selected':filters.symbol}">
              <v-select v-model="filters.symbol" :options="symbolsList" disabled></v-select>
              <span>Symbol</span>
            </div>
        </form>
        <div class="candle-chart-container" :class="{'loading': loadingNow.candle}">
          <highcharts :options="candleChartOptions" ref="candleChart"></highcharts>
        </div>
        <div class="ratio-chart-container" :class="{'loading': loadingNow.ratio}">
          <highcharts :options="ratioChartOptions" ref="ratioChart"></highcharts>
        </div>
        <div class="ratio-chart-container" :class="{'loading': loadingNow.delta}">
          <highcharts :options="deltaChartOptions" ref="deltaChart"></highcharts>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {Chart} from 'highcharts-vue'

import Highcharts from 'highcharts'
import stockInit from 'highcharts/modules/stock'
import moreInit from 'highcharts/highcharts-more'
stockInit(Highcharts)
moreInit(Highcharts)

import Datepicker from 'vuejs-datepicker';

import candleChartOptions from './candleChartOptions'
import ratioChartOptions from './ratioChartOptions'
import deltaChartOptions from './deltaChartOptions'

export default {
  name: 'Graph',
  props:['darkTheme'],
  components: {
    highcharts: Chart,
    Datepicker
  },
  data: () => ({
    loadingNow:{
      candle: false,
      ratio: false,
      delta: false
    },
    filters:{
        startDate: new Date().toUTCString(),
        endDate: new Date().toUTCString(),
        interval:'720',
        exchange: 'binance',
        symbol:'BTCUSDT',
    },
    candleChartOptions: candleChartOptions,
    ratioChartOptions: ratioChartOptions,
    deltaChartOptions: deltaChartOptions,
    responseData: {
      candle: {
      '0-1%':[],
      },
      ratio: {
      '0-1%':[],
      '1-2.5%':[],
      },
      delta: {
      '0-1%':[],
      '1-2.5%':[],
      }
    },
    responseHeaders: {
      candle: null,
      ratio: null,
      delta: null,
    }
  }),
  computed:{
    candleOHLC(){
      const OHLCArray = this.responseData.candle['0-1%'].map(item => {
        return {
            x: (item.timestamp * 1000),
            open: item.open_price,
            high: item.high_price,
            low: item.low_price,
            close: item.close_price,
        }
      })
      return OHLCArray
    },
    ratioLH(){
      const ratioEdge = 75;
      const GREEN_COLOR = '#26a69a';
      const RED_COLOR = '#ef5350';
      
      function ColoredColumn(item, color){
          this.x = item.timestamp * 1000;
          this.high = item.high_ratio;
          this.low = item.low_ratio;
          this.color = color;
          // set green color
          if (+item.high_ratio > ratioEdge) this.color = GREEN_COLOR;
          // set red color
          if (+item.low_ratio < -ratioEdge) this.color = RED_COLOR;
          // check if column cross 2 borders
          if (item.high_ratio > ratioEdge && item.low_ratio < -ratioEdge){
            this.color = (item.high_ratio > Math.abs(item.low_ratio)) ? GREEN_COLOR : RED_COLOR;
          }
      }
      const LHArray1 = this.responseData.ratio['0-1%'].map(item => {
        // light blue color
        return new ColoredColumn(item,'#7cb5ec')
      });
      const LHArray2 = this.responseData.ratio['1-2.5%'].map(item => {
        // dark blue color
        return new ColoredColumn(item,'rgb(47,41,182)')
      })
      return [LHArray1, LHArray2]
    },
    deltaLH(){
      const LHArray1 = this.responseData.delta['0-1%'].map(item => {
        return [item.timestamp * 1000, item.low_delta, item.high_delta]
      })
      const LHArray2 = this.responseData.delta['1-2.5%'].map(item => {
        return [item.timestamp * 1000, item.low_delta, item.high_delta]
      })
      return [LHArray1, LHArray2]
    },
    exchangesList(){
      return (this.responseHeaders.candle && this.responseHeaders.candle['page-amount'] != 0) ? this.responseHeaders.candle.exchanges.split(', ').sort() : [];
    },
    symbolsList(){
      return (this.responseHeaders.candle && this.responseHeaders.candle['page-amount'] != 0) ? this.responseHeaders.candle.symbols.split(', ').sort() : [];
    },
    intervalsList(){
      let fullIntervalsList = [
            {label: '6 Minutes', code: '360'},
            {label: '12 Minutes', code: '720'},
            {label: '1 Hour', code: '3600'},
            {label: '4 Hours', code: '14400'},
            {label: '1 Day', code: '86400'},
        ];
        let daysDiference = this.getDaysDifference(this.filters.startDate, this.filters.endDate);
        if (daysDiference < 3){
            return fullIntervalsList
        }
        else if(daysDiference >= 3 && daysDiference < 5){
            return fullIntervalsList.slice(1);
        }
        else if(daysDiference >= 5 && daysDiference <= 30){
            return fullIntervalsList.slice(2);
        }
        else{
            return fullIntervalsList.slice(3);
        }
    }
  },
  watch: {
    candleOHLC(newValue) {
       this.candleChartOptions.series[0].data = newValue
    },
    ratioLH(newValue) {
       this.ratioChartOptions.series[0].data = newValue[0];
       this.ratioChartOptions.series[1].data = newValue[1];
    },
    deltaLH(newValue) {
      this.deltaChartOptions.series[0].data = newValue[0];
      this.deltaChartOptions.series[1].data = newValue[1];
    },
    // update all charts
    filters: {
      handler(){
        this.getDataForCharts();
      },
     deep: true
    },
    darkTheme(){
      this.checkChartColors(50)
    },
  },
  methods:{
    checkChartColors(delay) {
      let lineColor = '#DDD9F9';
      let fontColor = '#667091';
      let fontColorHover = '#000000';
      let tooltipBackgroundColor = 'rgba(250,250,255,0.8)';
      let plotBandsBackgroundColor = 'rgba(204,223,255,0.3)';
      let buttonFillColor = '#ffffff';
      let buttonStrokeColor = '#41739D';
      if (this.darkTheme){
        lineColor = '#3A4464';
        fontColor = '#BCB6FF';
        fontColorHover = '#ffffff'
        tooltipBackgroundColor = 'rgba(58,68,100,0.8)';
        plotBandsBackgroundColor = 'rgba(15,83,141,0.3)';
        buttonFillColor = '#41739D';
        buttonStrokeColor = '#ffffff';
      }
      setTimeout(()=> {
        // equal styles for each chart
        ['candleChartOptions','ratioChartOptions','deltaChartOptions'].forEach(chart => {
          // lines 
          this[chart].yAxis.gridLineColor = lineColor;
          this[chart].xAxis.lineColor = lineColor;
          this[chart].xAxis.tickColor = lineColor;
          // fonts 
          this[chart].yAxis.labels.style.color = fontColor;
          this[chart].xAxis.labels.style.color = fontColor;
          // tooltip
          this[chart].tooltip.style.color = fontColor;
          this[chart].tooltip.borderColor = lineColor;
          this[chart].tooltip.backgroundColor = tooltipBackgroundColor;
          // button
          this[chart].chart.resetZoomButton.theme.fill = buttonFillColor;
          this[chart].chart.resetZoomButton.theme.stroke = buttonStrokeColor;
          this[chart].chart.resetZoomButton.theme.style.color = buttonStrokeColor;
          this[chart].chart.resetZoomButton.theme.states.hover.fill = buttonStrokeColor;
          this[chart].chart.resetZoomButton.theme.states.hover.style.color = buttonFillColor;
        });
        // equal styles for last 2 charts
        ['ratioChartOptions','deltaChartOptions'].forEach(chart => {
          this[chart].title.style.color = fontColor;
          this[chart].legend.itemStyle.color = fontColor;
          this[chart].legend.itemHoverStyle.color = fontColorHover;
        });
        // different styles
        this.ratioChartOptions.yAxis.plotBands[0].color = plotBandsBackgroundColor;
        this.deltaChartOptions.yAxis.plotBands[0].color = plotBandsBackgroundColor;
      }, delay)
    },
    checkDateParams(){
      let daysDiference = this.getDaysDifference(this.filters.startDate, this.filters.endDate);
      if (daysDiference >= 3 && daysDiference < 5){
          if (this.filters.interval == '360'){
              this.filters.interval = '720';
              return false;
          }
      }
      else if(daysDiference >= 5 && daysDiference <= 30){
          if (+this.filters.interval <= 720){
              this.filters.interval = '3600';
              return false;
          }
      }
      else if (daysDiference > 30){
          if (+this.filters.interval <= 3600){
              this.filters.interval = '14400';
              return false;
          }
      }
      return true
    },
    getData(page = 1, name, chartName){
        let queryParams = {
          page:page,
          startDate: this.moment(this.filters['startDate']).utc().format('YYYY-MM-DD'),
          endDate: this.moment(this.filters['endDate']).utc().format('YYYY-MM-DD'),
          exchange: this.filters.exchange,
          symbol: this.filters.symbol,
          indicator_type: 'orderbook',
          name: name,
          interval: this.filters.interval
        };
        return axios.get(`indicators/${chartName}`,{params:queryParams}).then(response => {
          this.responseHeaders[chartName] = response.headers
          return response.data
        });
    },
    async getDataFromAllPages(name, chartName){
      // reset zoom
      if (chartName === 'candle' || name === '1-2.5%'){
        this.$refs[chartName + 'Chart'].chart.zoomOut();
      }

      // check query params
      if (!this.checkDateParams()) return;
      let allData = await this.getData(1, name, chartName);
      for (let page = 2; page <= +this.responseHeaders[chartName]['page-amount']; page++) {
        allData = [...allData, ...await this.getData(page, name, chartName)];
      }
      this.responseData[chartName][name] = allData;
      this.loadingNow[chartName] = false;
      // turn on zoom 
      if (chartName === 'delta' && name === '1-2.5%'){
        this.setZoomFunctionsForCharts();
      }

    },
    getDataForCharts(){
      this.removeZoomFunctionsFromCharts();
      // data for Candle
      this.loadingNow['candle'] = true;
      this.getDataFromAllPages('0-1%', 'candle');
      // get data for Ratio 
      this.loadingNow['ratio'] = true;
      this.getDataFromAllPages('0-1%', 'ratio');
      this.getDataFromAllPages('1-2.5%', 'ratio');
      // get data for Delta
      this.loadingNow['delta'] = true;
      this.getDataFromAllPages('0-1%', 'delta');
      this.getDataFromAllPages('1-2.5%', 'delta');
    },
    setZoomFunctionsForCharts() {
      // get refs for every chart
      const candleChart = this.$refs.candleChart.chart;
      const ratioChart = this.$refs.ratioChart.chart;
      const deltaChart = this.$refs.deltaChart.chart;
      // set zoom function for every chart
      ['candleChartOptions','ratioChartOptions','deltaChartOptions'].forEach(chart => {
        this[chart].xAxis.events['afterSetExtremes'] = (event) => {
          // zoom 2 another charts
          if (!chart.includes('candleChart')) candleChart.xAxis[0].setExtremes(event.min, event.max, true, false);
          if (!chart.includes('ratioChart')) ratioChart.xAxis[0].setExtremes(event.min, event.max, true, false);
          if (!chart.includes('deltaChart')) deltaChart.xAxis[0].setExtremes(event.min, event.max, true, false);
        }
      });
    },
    removeZoomFunctionsFromCharts(){
      ['candleChartOptions','ratioChartOptions','deltaChartOptions'].forEach(chart => {
        this[chart].xAxis.events['afterSetExtremes'] = undefined;
      });
    }
  },
  mounted(){
    this.getDataForCharts();
    this.checkChartColors(0);
  },
  beforeDestroy(){
    // remove zoom functons before component destroy
    this.removeZoomFunctionsFromCharts();
  }
};
</script>
<style lang="scss">
.graph-page{
  .row{
    display: flex;
    align-items: center;
    h3{
      margin-right: 10px;
    }
  }
  form{
    display: flex;
    justify-content: space-between;
    max-width: 875px;
    flex-wrap: wrap;
    @media (max-width: 950px) {
      justify-content: flex-start;
    }
    & > *{
      width: 168px !important;
      margin-bottom: 15px;
      @media (max-width: 950px) {
        width: calc(33.333% - 10px) !important;
        margin-right: 10px;
      }
      @media (max-width: 600px) {
        width: 100% !important;
      }
    }
  }
  .loading{
    pointer-events: none;
    position: relative;
    &::before{
      content: "";
      width: 500%;
      height: 100%;
      position: absolute;
      z-index: 5;
      background: var(--loading-bg);
    }
    &::after{
      content: "";
      width: 64px;
      height: 64px;
      background: url('../../../assets/img/loader.svg') no-repeat;
      position: absolute;
      z-index: 10;
      left:50%;
      top:50%;
      transform: translate(-50%,-50%);
    }
  }
}
</style>